import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTables } from '../hooks/useTables';
import { supabase } from '../services/supabase';

export default function CashPaymentPage() {
  const { tableId } = useParams<{ tableId?: string }>();
  const { session } = useAuth();
  const navigate = useNavigate();
  const { tables, loading: tablesLoading } = useTables(session?.storeId ?? '');

  const [selectedTable, setSelectedTable] = useState<string>(tableId ?? '');
  const [amount, setAmount] = useState('');
  const [tip, setTip] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    const numAmount = parseFloat(amount);
    if (!selectedTable || isNaN(numAmount) || numAmount <= 0) {
      setError('Veuillez renseigner la table et le montant.');
      return;
    }
    if (!session) return;
    if (!session.token) {
      setError('Session expirée — reconnecte-toi pour encaisser (sealing fiscal requis).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const numTip = parseFloat(tip) || 0;
      // Encaissement cash via edge function : crée l'ordre + la ligne payments
      // ET déclenche le sealing fiscal DGI (cf. tapnpay-staff-cash-sale).
      // Remplace l'ancien insert DB direct qui contournait le sealing = non conforme.
      const { error: fnError } = await supabase.functions.invoke('tapnpay-staff-cash-sale', {
        body: { table_id: selectedTable, amount: numAmount, tip: numTip },
        headers: { Authorization: `Bearer ${session.token}` },
      });
      if (fnError) throw fnError;
      navigate(-1);
    } catch (e) {
      setError((e as Error).message ?? 'Erreur lors de l\'encaissement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header
        className="sticky top-0 z-30 bg-surface flex items-center gap-3 px-4 h-12"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="min-h-[40px] min-w-[40px] flex items-center justify-center text-on-surface-variant"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
        </button>
        <p className="text-sm font-bold text-on-surface-strong">Encaissement cash</p>
      </header>

      <div className="flex-1 p-4 space-y-4">
        {!tableId && (
          <div className="bg-surface" style={{ border: '0.5px solid #e5e7eb' }}>
            <div className="px-4 py-2" style={{ borderBottom: '0.5px solid #e5e7eb' }}>
              <p className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Table</p>
            </div>
            {tablesLoading ? (
              <div className="p-4 text-sm text-on-surface-variant">Chargement…</div>
            ) : (
              <div className="max-h-[200px] overflow-y-auto">
                {tables.map((t, i) => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTable(t.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 min-h-[44px] text-left transition-none ${
                      selectedTable === t.id ? 'bg-inverse-surface' : 'bg-surface'
                    }`}
                    style={{ borderBottom: i < tables.length - 1 ? '0.5px solid #e5e7eb' : undefined }}
                  >
                    <span className={`text-sm ${selectedTable === t.id ? 'text-inverse-on-surface font-semibold' : 'text-on-surface'}`}>
                      Table {t.table_number}{t.section ? ` · ${t.section}` : ''}
                    </span>
                    <span className={`text-xs ${selectedTable === t.id ? 'text-inverse-on-surface' : 'text-on-surface-variant'}`}>
                      {t.capacity} pers
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {tableId && (
          <div className="bg-surface px-4 py-3 flex items-center justify-between" style={{ border: '0.5px solid #e5e7eb' }}>
            <span className="text-xs text-on-surface-variant">Table</span>
            <span className="text-sm font-bold text-on-surface-strong">
              {tables.find((t) => t.id === tableId)
                ? `Table ${tables.find((t) => t.id === tableId)!.table_number}`
                : '—'}
            </span>
          </div>
        )}

        <div className="bg-surface" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="px-4 py-2" style={{ borderBottom: '0.5px solid #e5e7eb' }}>
            <p className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Montant</p>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="flex-1 min-h-[44px] px-3 text-lg font-bold tabular-nums text-on-surface-strong bg-surface"
              style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
              inputMode="decimal"
            />
            <span className="text-sm text-on-surface-variant shrink-0">MAD</span>
          </div>
        </div>

        <div className="bg-surface" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="px-4 py-2" style={{ borderBottom: '0.5px solid #e5e7eb' }}>
            <p className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Pourboire (optionnel)</p>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <input
              type="number"
              value={tip}
              onChange={(e) => setTip(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="flex-1 min-h-[44px] px-3 text-sm tabular-nums text-on-surface bg-surface"
              style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
              inputMode="decimal"
            />
            <span className="text-sm text-on-surface-variant shrink-0">MAD</span>
          </div>
        </div>

        {error && (
          <p className="text-sm text-error px-1">{error}</p>
        )}

        <button
          onClick={() => void handleSubmit()}
          disabled={saving || !selectedTable || !amount}
          className="w-full min-h-[52px] flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-on-primary bg-primary disabled:opacity-40"
        >
          <span className="material-symbols-outlined text-[18px]">payments</span>
          {saving ? 'Enregistrement…' : 'Encaisser'}
        </button>
      </div>
    </div>
  );
}
